<template>
  <div class="kiosk-dashboard">
    <!-- Page Header -->
    <div class="d-flex justify-space-between align-center mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold">
          Kiosk Dashboard
        </h1>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Real-time kiosk monitoring and visitor analytics
        </p>
      </div>

      <div class="d-flex flex-column gap-3 align-center">
        <!-- Auto-refresh indicator -->
        <VChip
          :color="autoRefreshEnabled ? 'success' : 'default'"
          size="small"
          label
        >
          <VIcon
            :icon="autoRefreshEnabled ? 'tabler-refresh' : 'tabler-refresh-off'"
            size="16"
            class="me-1"
          />
          Auto-refresh {{ autoRefreshEnabled ? 'ON' : 'OFF' }}
        </VChip>

        <!-- Last updated time -->
        <span class="text-caption text-medium-emphasis">
          Last updated: {{ lastUpdatedTime }}
        </span>
      </div>
    </div>

    <!-- Summary Cards Row -->
    <VRow class="mb-6">
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="stat-card">
          <VCardText>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-medium-emphasis mb-1">
                  Check In Session
                </p>
                <h2 class="text-h4 font-weight-bold">
                  {{ summaryStats?.check_in_sessions }}
                </h2>
              </div>
              <VAvatar
                color="warning"
                variant="tonal"
                size="48"
              >
                <VIcon
                  icon="tabler-door-enter"
                  size="28"
                />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="stat-card">
          <VCardText>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-medium-emphasis mb-1">
                  Check Out Session
                </p>
                <h2 class="text-h4 font-weight-bold">
                  {{ summaryStats?.check_out_sessions }}
                </h2>
              </div>
              <VAvatar
                color="success"
                variant="tonal"
                size="48"
              >
                <VIcon
                  icon="tabler-door-exit"
                  size="28"
                />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="stat-card">
          <VCardText>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-medium-emphasis mb-1">
                  Active Sessions
                </p>
                <h2 class="text-h4 font-weight-bold">
                  {{ summaryStats?.current_active_sessions }}
                </h2>
              </div>
              <VAvatar
                color="info"
                variant="tonal"
                size="48"
              >
                <VIcon
                  icon="tabler-user-bolt"
                  size="28"
                />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="stat-card">
          <VCardText>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-body-2 text-medium-emphasis mb-1">
                  Completed Sessions
                </p>
                <h2 class="text-h4 font-weight-bold">
                  {{ summaryStats?.total_sessions }}
                </h2>
              </div>
              <VAvatar
                color="primary"
                variant="tonal"
                size="48"
              >
                <VIcon
                  icon="tabler-user-check"
                  size="28"
                />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Kiosk Cards Section -->
    <div class="mb-4">
      <h5 class="text-h5 font-weight-semibold mb-4">
        Kiosk Devices
      </h5>
    </div>

    <!-- Loading State -->
    <div
      v-if="isFetchingKiosks"
      class="d-flex justify-center py-12"
    >
      <VProgressCircular
        indeterminate
        size="64"
        color="primary"
      />
    </div>
    <!-- Kiosk Cards Grid -->
    <VRow v-else-if="kiosks?.length > 0">
      <VCol
        v-for="(kiosk, kioskIndex) in kiosks"
        :key="kioskIndex"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <VCard
          class="kiosk-card"
          :class="getKioskCardClass(kiosk.status)"
        >
          <VCardText>
            <!-- Status Badge -->
            <div class="d-flex justify-space-between align-center mb-3">
              <VChip
                class="text-capitalize"
                :color="getStatusColor(kiosk.status)"
                size="small"
                label
              >
                {{ kiosk.status }}
              </VChip>
              <VIcon
                :icon="getStatusIcon(kiosk.status)"
                :color="getStatusColor(kiosk.status)"
                size="20"
              />
            </div>

            <!-- Device Name -->
            <h6 class="text-h6 font-weight-bold mb-2">
              {{ kiosk.name }}
            </h6>

            <!-- Location -->
            <div class="d-flex align-center mb-3">
              <VIcon
                icon="tabler-map-pin"
                size="16"
                class="me-1 text-medium-emphasis"
              />
              <span class="text-body-2 text-medium-emphasis">
                {{ kiosk.visitor_location?.name || 'N/A' }}
              </span>
            </div>

            <VDivider class="my-3" />

            <!-- Stats -->
            <div class="mb-3">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2 text-medium-emphasis">Last Heartbeat:</span>
                <span class="text-body-2 font-weight-medium">
                  {{ formatLastHeartbeat(kiosk.last_heartbeat_at) }}
                </span>
              </div>
              <!--
                <div class="d-flex justify-space-between align-center">
                <span class="text-body-2 text-medium-emphasis">Today Transactions:</span>
                <VChip
                color="info"
                size="x-small"
                variant="tonal"
                >
                {{ kiosk.current_visitors }}
                </VChip>
                </div> 
              -->
            </div>

            <!-- Action Button -->
            <!-- openAnalytics(kiosk -->
            <VBtn
              block
              color="primary"
              variant="tonal"
              size="small"
              @click="router.push({ name: 'app-care-visit-kiosk-analytics', params: { id: kiosk.id } })"
            >
              <VIcon
                icon="tabler-chart-bar"
                size="18"
                class="me-1"
              />
              View Analytics
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
    <VCard v-else>
      <VCardText class="text-center">
        No Kiosk Found
      </VCardText>
    </VCard>

    <!-- Live Visits Section -->
    <div class="mt-8 mb-4">
      <h5 class="text-h5 font-weight-semibold mb-4">
        Visit Logs
      </h5>
    </div>

    <VCard>
      <VCardText class="pa-0">      
        <VDataTableServer
          v-model:items-per-page="visitorSessionItemPerPage"
          v-model:page="visitorSessionPageNumber"
          :items="visitorSessions ?? []"
          :items-length="visitorSessionLength"
          :headers="visitorSessionTableHeader"
          fixed-header
          class="text-no-wrap mb-4"
          :loading="isFetchingVisitorSession"
        >
          <template #item.kiosk="{ item }">
            {{ item.kiosk?.name ?? 'N/A' }}
          </template>
          <template #item.action_type="{ item }">
            {{ item.action_type }}
          </template>
        </VDataTableServer>
      </VCardText>
    </VCard>
  </div>
</template>

<script setup>
import { createUrl } from '@/@core/composable/createUrl'
import { useApi } from '@/composables/useApi'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: "Dashboard",
    subject: "care_visit.menu",
    action: "dashboard",
  },
})

const router = useRouter()

// Reactive data
const loading = ref(false)
const loadingAnalytics = ref(false)
const loadingLiveVisits = ref(false)
const autoRefreshEnabled = ref(true)
const analyticsDialog = ref(false)
const selectedKiosk = ref(null)
const lastUpdated = ref(new Date())

// Auto-refresh intervals
let mainRefreshInterval = null
let analyticsRefreshInterval = null

// Summary Statistics
const summaryStats = ref()

const {
  isFetching: isFetchingSummaryStats,
  execute: fetchSummaryStats,
} = useApi(createUrl(`/care-visit/dashboard/overview`), {
  afterFetch: ({ data }) => {
    summaryStats.value = data
  },
})

// Load Kiosk Data
const {
  isFetching: isFetchingKiosks,
  data: kiosks,
  execute: fetchKiosks,
} = useApi(createUrl(`/care-visit/kiosks`))

// Kiosk analytics data
const kioskAnalytics = ref({
  checkIn: 0,
  checkOut: 0,
  totalVisits: 0,
})

// Visitor list for selected kiosk
const visitorList = ref([
  // Sample data - will be replaced with API call
])

const visitorSessionItemPerPage = ref(10)
const visitorSessionPageNumber = ref(1)

const visitorSessionTableHeader = [
  {
    title: 'Visitor Pass Code',
    key: 'visitor_q_r_code.code',
  },
  {
    title: 'Visitor Name',
    key: 'visitor.name',
  },
  {
    title: 'Patient UHID',
    key: 'episode.customer.uhid',
  },
  {
    title: 'Patient Name',
    key: 'episode.customer.name',
  },
  {
    title: 'Kiosk',
    key: 'kiosk',
  },
  {
    title: 'Action Type',
    key: 'action_type',
  },
]

const visitorSessions = ref([])

const {
  isFetching: isFetchingVisitorSessions,
  execute: fetchVisitorSessions,
} = useApi(createUrl('/care-visit/dashboard/visitor-access-logs', {
  query: {
    per_page: visitorSessionItemPerPage.value,
  },
}), {
  afterFetch: (({ data }) => {
    visitorSessions.value = data
  }),
})

const visitorSessionLength = computed(() => visitorSessions.value?.length ?? 0)

// Computed
const lastUpdatedTime = computed(() => {
  return lastUpdated.value.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
})

// Methods

/**
 * Fetch summary statistics
 * TODO: Replace with actual API call
 */
// const fetchSummaryStats = async () => {
//   try {
//     // API call placeholder
//     // const response = await fetch('/api/kiosk/summary-stats')
//     // const data = await response.json()
//     // summaryStats.value = data

//     // Mock data for now
//     // summaryStats.value = {
//     //   totalVisits: 156,
//     //   totalCheckIn: 98,
//     //   totalCheckOut: 58,
//     // }

//     lastUpdated.value = new Date()
//   } catch (error) {
//     console.error('Error fetching summary stats:', error)
//   }
// }

/**
 * Fetch all kiosks data
 * TODO: Replace with actual API call
 */
// const fetchKiosks = async () => {
//   loading.value = true

//   try {
//     // API call placeholder
//     // const response = await fetch('/api/kiosk/devices')
//     // const data = await response.json()
//     // kiosks.value = data

//     // Using mock data for now (already set in ref)
//     lastUpdated.value = new Date()
//   } catch (error) {
//     console.error('Error fetching kiosks:', error)
//   } finally {
//     loading.value = false
//   }
// }

/**
 * Fetch live visits data
 * TODO: Replace with actual API call
 */
// const fetchLiveVisits = async () => {
//   loadingLiveVisits.value = true

//   try {
//     // API call placeholder
//     // const response = await fetch('/api/kiosk/live-visits')
//     // const data = await response.json()
//     // liveVisits.value = data

//     // Using mock data for now (already set in ref)
//     lastUpdated.value = new Date()
//   } catch (error) {
//     console.error('Error fetching live visits:', error)
//   } finally {
//     loadingLiveVisits.value = false
//   }
// }

/**
 * Fetch kiosk-specific analytics
 * TODO: Replace with actual API call
 */
const fetchKioskAnalytics = async kioskId => {
  loadingAnalytics.value = true

  try {
    // API call placeholder
    // const response = await fetch(`/api/kiosk/${kioskId}/analytics`)
    // const data = await response.json()
    // kioskAnalytics.value = data.stats
    // visitorList.value = data.visitors

    // Mock data for now
    kioskAnalytics.value = {
      checkIn: 45,
      checkOut: 38,
      totalVisits: 45,
    }

    visitorList.value = [
      {
        id: 1,
        visitorName: 'John Doe',
        passCode: 'VP001234',
        checkInTime: new Date(Date.now() - 7200000), // 2 hours ago
        checkOutTime: new Date(Date.now() - 3600000), // 1 hour ago
      },
      {
        id: 2,
        visitorName: 'Jane Smith',
        passCode: 'VP001235',
        checkInTime: new Date(Date.now() - 5400000), // 1.5 hours ago
        checkOutTime: null, // Still active
      },
      {
        id: 3,
        visitorName: 'Robert Johnson',
        passCode: 'VP001236',
        checkInTime: new Date(Date.now() - 10800000), // 3 hours ago
        checkOutTime: new Date(Date.now() - 7200000), // 2 hours ago
      },
    ]
  } catch (error) {
    console.error('Error fetching kiosk analytics:', error)
  } finally {
    loadingAnalytics.value = false
  }
}

/**
 * Refresh all dashboard data
 */
const refreshDashboard = async () => {
  await Promise.all([
    fetchSummaryStats(),
    fetchKiosks(),
    fetchVisitorSessions(),
  ])
}

/**
 * Refresh analytics data (called every minute when dialog is open)
 */
const refreshAnalytics = async () => {
  if (selectedKiosk.value && analyticsDialog.value) {
    await fetchKioskAnalytics(selectedKiosk.value.id)
  }
}

/**
 * Open analytics dialog for a kiosk
 */
const openAnalytics = async kiosk => {
  selectedKiosk.value = kiosk
  analyticsDialog.value = true

  // Fetch analytics data
  await fetchKioskAnalytics(kiosk.id)

  // Start auto-refresh for analytics (every 1 minute)
  analyticsRefreshInterval = setInterval(refreshAnalytics, 60000)
}

/**
 * Close analytics dialog
 */
const closeAnalytics = () => {
  analyticsDialog.value = false
  selectedKiosk.value = null

  // Clear analytics refresh interval
  if (analyticsRefreshInterval) {
    clearInterval(analyticsRefreshInterval)
    analyticsRefreshInterval = null
  }
}

/**
 * Get status color based on kiosk status
 */
const getStatusColor = status => {
  const statusMap = {
    'Online': 'success',
    'Offline': 'error',
    'Idle': 'warning',
    'Error': 'error',
  }

  return statusMap[status] || 'default'
}

/**
 * Get status icon based on kiosk status
 */
const getStatusIcon = status => {
  const iconMap = {
    'Online': 'tabler-circle-check',
    'Offline': 'tabler-circle-x',
    'Idle': 'tabler-clock',
    'Error': 'tabler-alert-circle',
  }

  return iconMap[status] || 'tabler-help-circle'
}

/**
 * Get card class based on kiosk status
 */
const getKioskCardClass = status => {
  return status === 'Offline' ? 'kiosk-card-offline' : ''
}

/**
 * Format last heartbeat time
 */
const formatLastHeartbeat = date => {
  if (!date) return 'Never'

  const now = new Date()
  const diffMs = now - new Date(date)
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'Just now'
  if (diffMins === 1) return '1 min ago'
  if (diffMins < 60) return `${diffMins} mins ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours === 1) return '1 hour ago'
  if (diffHours < 24) return `${diffHours} hours ago`

  return 'Never'

  // return date.toLocaleDateString() || 'Never'
}

/**
 * Format date and time
 */
const formatDateTime = date => {
  if (!date) return '-'

  const d = new Date(date)

  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Lifecycle hooks
onMounted(async () => {
  // Initial data fetch
  await refreshDashboard()

  // Start auto-refresh (every 1 minute)
  if (autoRefreshEnabled.value) {
    mainRefreshInterval = setInterval(refreshDashboard, 60000)
  }
})

onUnmounted(() => {
  // Clear all intervals
  if (mainRefreshInterval) {
    clearInterval(mainRefreshInterval)
  }

  if (analyticsRefreshInterval) {
    clearInterval(analyticsRefreshInterval)
  }
})
</script>

<style scoped>
.kiosk-dashboard {
  padding: 24px;
}

.gap-3 {
  gap: 12px;
}

.text-medium-emphasis {
  opacity: 0.68;
}

.stat-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.stat-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 10%);
  transform: translateY(-4px);
}

.kiosk-card {
  block-size: 100%;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.kiosk-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 10%);
  transform: translateY(-4px);
}

.kiosk-card-offline {
  opacity: 0.7;
}

.live-visit-row {
  transition: background-color 0.2s ease-in-out;
}

.live-visit-row:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}
</style>
